import { SEO } from '@/components/seo'

export function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy - AI Detector Arena"
        description="Privacy Policy for AI Detector Arena. Learn how we handle your data and protect your privacy when using our AI detector comparison platform."
        canonical="/privacy"
      />

      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Privacy Policy</h1>
            <p className="text-gray-500 mb-8">Last updated: January 1, 2026</p>

            <div className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Overview</h2>
              <p className="text-gray-600 mb-4">
                At AI Detector Arena, we take your privacy seriously. This Privacy Policy explains how we collect,
                use, and protect your information when you use our Service.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Images</h3>
              <p className="text-gray-600 mb-4">
                Images you upload for processing are temporarily stored only for the duration of processing.
                They are automatically deleted from our servers immediately after you download the result
                or within 1 hour, whichever comes first. We do not retain, analyze, or use your images
                for any other purpose.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Account Information</h3>
              <p className="text-gray-600 mb-4">
                If you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>Payment information (processed by Stripe, we don't store card details)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Usage Data</h3>
              <p className="text-gray-600 mb-4">
                We collect anonymous usage data including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Number of images processed</li>
                <li>Feature usage statistics</li>
                <li>Error logs for debugging</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Technical Data</h3>
              <p className="text-gray-600 mb-4">
                Like most websites, we collect:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>IP address (anonymized)</li>
                <li>Browser type and version</li>
                <li>Device type</li>
                <li>Referring website</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Provide the AI detector comparison service</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send important service updates</li>
                <li>Improve our benchmarks and user experience</li>
                <li>Prevent abuse and fraud</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Data Sharing</h2>
              <p className="text-gray-600 mb-4">
                We do not sell your personal information. We only share data with:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li><strong>Stripe</strong> - Payment processing</li>
                <li><strong>Analytics providers</strong> - Anonymous usage statistics</li>
                <li><strong>Law enforcement</strong> - Only when legally required</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Cookies</h2>
              <p className="text-gray-600 mb-4">
                We use essential cookies for authentication and preferences. We also use analytics
                cookies to understand how visitors use our site. You can disable cookies in your
                browser settings.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Data Security</h2>
              <p className="text-gray-600 mb-4">
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>TLS/SSL encryption for all data in transit</li>
                <li>Encrypted storage for sensitive data</li>
                <li>Regular security audits</li>
                <li>Automatic image deletion after processing</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Your Rights</h2>
              <p className="text-gray-600 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and data</li>
                <li>Export your data</li>
                <li>Opt out of marketing communications</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">GDPR Compliance</h2>
              <p className="text-gray-600 mb-4">
                For users in the European Union, we comply with GDPR requirements. You can exercise
                your data rights by contacting us at contact@aidetectarena.com.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">CCPA Compliance</h2>
              <p className="text-gray-600 mb-4">
                For California residents, we comply with CCPA requirements. You have the right to
                know what personal information we collect and to request deletion.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Children's Privacy</h2>
              <p className="text-gray-600 mb-4">
                Our Service is not intended for users under 13 years of age. We do not knowingly
                collect information from children.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Changes to This Policy</h2>
              <p className="text-gray-600 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of significant
                changes via email or through the Service.
              </p>

              <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Contact Us</h2>
              <p className="text-gray-600 mb-4">
                For privacy-related questions or requests, contact us at:{' '}
                <a href="mailto:contact@aidetectarena.com" className="text-primary-600 hover:underline">
                  contact@aidetectarena.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
